import React, { useState } from "react";
import { InputGroup, Form } from "react-bootstrap";
import InvoiceAppointmentModal from "./AppointmentPicker"; // Adjust path as needed
import { isLineDateOnly } from "../../utils/appointmentTimes";

export default function AppointmentDateInput({ 
  patientId, 
  fetchAppointments, 
  onAppointmentSelect,
  initial,
  error = "",
  value = null // Current appointment data
}) {
  const [showModal, setShowModal] = useState(false);

  // Numeric date, no leading zeros: D-M-YYYY
  const fmtDate = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    const d = new Date(dateTimeStr);
    if (Number.isNaN(d.getTime())) return dateTimeStr;
    return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;
  };

  // 24h time, no leading hour zero: H:MM
  const fmtTime = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    try {
      return new Date(dateTimeStr)
        .toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
        .replace(/^0/, "");
    } catch {
      return "";
    }
  };

  // Compact range: collapse the date when start/end fall on the same day.
  //   Same date:      "8:30-9:00 | 2-7-2026"
  //   Different date: "2-7-2026 8:30 - 3-7-2026 9:00"
  //   No end value:   "8:30 | 2-7-2026"
  const fmtRange = (startStr, endStr) => {
    if (!startStr) return "";
    const startDate = fmtDate(startStr);
    const startTime = fmtTime(startStr);
    if (!endStr) return `${startTime} | ${startDate}`;
    const endDate = fmtDate(endStr);
    const endTime = fmtTime(endStr);
    if (startDate === endDate) {
      return `${startTime}-${endTime} | ${startDate}`;
    }
    return `${startDate} ${startTime} - ${endDate} ${endTime}`;
  };

  // Format display text based on current value structure.
  // `value` arrives in two shapes:
  //   - CreateInvoice passes split { startDate, startTime, endDate, endTime }
  //     (see lineApptValue) — this is the shape used on invoice lines.
  //   - other callers pass appointmentCustomStart/End or appointmentStartSet/EndSet.
  // Normalise both to ISO so everything flows through the compact formatter.
  const getDisplayText = () => {
    if (!value) return "";

    const startISO =
      (value.useCustomDate
        ? value.appointmentCustomStart
        : value.appointmentStartSet) ||
      (value.startDate
        ? `${value.startDate}T${value.startTime || "00:00"}`
        : "") ||
      (!value.useCustomDate && value.appointmentDateSet
        ? `${value.appointmentDateSet}T00:00`
        : "");

    const endISO =
      (value.useCustomDate
        ? value.appointmentCustomEnd
        : value.appointmentEndSet) ||
      (value.endDate ? `${value.endDate}T${value.endTime || "00:00"}` : "");

    if (!startISO) return "";

    // Date-only when the shared helper says so, or when both ends sit at
    // midnight (the split-field shape, which isLineDateOnly can't read).
    const isMidnight = (t) => !t || t === "00:00";
    const dateOnly =
      isLineDateOnly(value) ||
      (isMidnight(value.startTime) &&
        isMidnight(value.endTime) &&
        !!(value.startDate || value.endDate));

    if (dateOnly) {
      const startDate = fmtDate(startISO);
      const endDate = endISO ? fmtDate(endISO) : "";
      return endDate && endDate !== startDate
        ? `${startDate} - ${endDate}`
        : startDate;
    }

    return fmtRange(startISO, endISO);
  };

  const handleModalConfirm = (appointmentData) => {
    onAppointmentSelect?.(appointmentData);
    setShowModal(false);
  };

  const displayText = getDisplayText();

  return (
    <>
      <InputGroup 
        style={{ cursor: 'pointer' }} 
        onClick={() => setShowModal(true)}
        className={error ? 'border-danger' : ''}
      >
        <InputGroup.Text 
          style={{ 
            backgroundColor: error ? '#f8d7da' : 'rgb(206.6, 226, 254.6)',
            border: `1px solid ${error ? '#dc3545' : 'rgb(206.6, 226, 254.6)'}`,
            borderRight: 'none',
            cursor: 'pointer'
          }}
        >
          <i className="fa-regular fa-calendar-days text-primary"></i>
        </InputGroup.Text>
        <Form.Control
          type="text"
          placeholder="Click to select appointment date & time"
          value={displayText}
          readOnly
          style={{
            borderLeft: 'none',
            height: '30px',
            fontSize: '0.65rem',
            cursor: 'pointer',
            backgroundColor: 'white',
            borderColor: error ? '#dc3545' : '#ced4da'
          }}
          aria-invalid={!!error}
          isInvalid={!!error}
        />
      </InputGroup>

      {value?.isInvoiced && (
        <div className="text-warning small mt-1">
          <i className="fa-solid fa-triangle-exclamation me-1"></i>
          This appointment was already invoiced before.
        </div>
      )}

      <InvoiceAppointmentModal
        show={showModal}
        onHide={() => setShowModal(false)}
        patientId={patientId}
        fetchAppointments={fetchAppointments}
        initial={initial}
        onConfirm={handleModalConfirm}
        initialValue={value} // Pass current value to modal for editing
      />
    </>
  );
}